/**
 * TasksDashboard
 * Layout matches old ccms_taskmanager dashboard:
 * - Top: Stats cards (2x2 grid + overdue full-width)
 * - Middle-left: Due Soon / To-Do list
 * - Middle-right: Active tasks (in progress)
 * - Bottom: Task summary tables grouped by status
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack, Title, Text, Group, Grid, Card, Badge, Button,
  ThemeIcon, SimpleGrid,
} from '@/components/ui';
import { Avatar, Divider, EmptyState } from '@/components/ui/data-display';
import { Skeleton } from '@/components/ui';
import { Accordion } from '@/components/ui/navigation';
import { ScrollArea } from '@/components/ui/layout';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations/transitions';
import {
  IconPlus, IconRefresh, IconNote, IconEye, IconEyeCheck,
  IconPlayerPlay, IconCheck, IconAlertCircle, IconClock,
  IconChecklist, IconArrowRight,
} from '@tabler/icons-react';
import { useTaskPortalTaskService } from '@actschurch/ccms-sdk';
import { useTaskStore } from '@/stores/taskStore';
import { useTaskLookups } from '@/hooks/tasks/useTaskLookups';
import {
  TaskStatusBadge, TaskPriorityBadge, DueDateDisplay, SelfTaskBadge,
} from '../components';
import { TASK_STATUS, COMPLETED_STATUSES } from '../utils/taskConstants';
import { sortByDueDate, normalizeTasks } from '../utils/taskHelpers';
import dayjs from 'dayjs';

export default function TasksDashboard() {
  const navigate = useNavigate();
  const portalTaskService = useTaskPortalTaskService();
  const setQuickTaskModalOpen = useTaskStore((s) => s.setQuickTaskModalOpen);
  const setReturnPath = useTaskStore((s) => s.setReturnPath);
  const { statuses } = useTaskLookups();

  const { data: allTasks = [], isLoading, refetch } = useQuery({
    queryKey: ['tasks', 'portal', 'my-tasks'],
    queryFn: async () => normalizeTasks(await portalTaskService.getMyTasks({ take: 10000 } as any)),
  });

  // Stats
  const stats = useMemo(() => {
    const now = dayjs();
    const endOfWeek = now.endOf('week');
    return {
      unseen: allTasks.filter((t) => t.statusID === TASK_STATUS.UNSEEN).length,
      seenIt: allTasks.filter((t) => t.statusID === TASK_STATUS.SEEN_IT).length,
      onIt: allTasks.filter((t) => t.statusID === TASK_STATUS.ON_IT).length,
      doneIt: allTasks.filter((t) => t.statusID === TASK_STATUS.DONE_IT).length,
      overdue: allTasks.filter((t) => t.isOverdue).length,
      dueThisWeek: allTasks.filter((t) => {
        if (!t.dueDate || t.statusID === TASK_STATUS.DONE_IT || t.statusID === TASK_STATUS.CLOSED) return false;
        const due = dayjs(t.dueDate);
        return due.isBefore(endOfWeek) && due.isAfter(now.subtract(1, 'day'));
      }).length,
      total: allTasks.length,
    };
  }, [allTasks]);

  // To-do: upcoming tasks sorted by due date (exclude done/closed)
  const todoTasks = useMemo(
    () => sortByDueDate(allTasks.filter((t) => !COMPLETED_STATUSES.includes(t.statusID as any))).slice(0, 12),
    [allTasks]
  );

  // Active: tasks user is working on
  const activeTasks = useMemo(
    () => allTasks.filter((t) => t.statusID !== TASK_STATUS.UNSEEN && t.statusID !== TASK_STATUS.DONE_IT && t.statusID !== TASK_STATUS.CLOSED)
      .sort((a, b) => { if (a.isOverdue && !b.isOverdue) return -1; if (!a.isOverdue && b.isOverdue) return 1; return 0; })
      .slice(0, 10),
    [allTasks]
  );

  // Group by status for summary
  const tasksByStatus = useMemo(() => {
    const groups: Record<number, typeof allTasks> = {};
    allTasks.forEach((t) => {
      if (!groups[t.statusID]) groups[t.statusID] = [];
      groups[t.statusID].push(t);
    });
    return groups;
  }, [allTasks]);

  const goToTask = (taskId: number) => {
    setReturnPath('/app/tasks');
    navigate(`/app/tasks/view/${taskId}`);
  };

  if (isLoading) {
    return (
      <FadeIn>
        <Stack gap="lg">
          <SimpleGrid cols={{ base: 2, md: 3 }}>{[1,2,3,4,5].map(i => <Skeleton key={i} height={90} radius="md" />)}</SimpleGrid>
          <Grid><Grid.Col span={{ base: 12, md: 7 }}><Skeleton height={400} /></Grid.Col><Grid.Col span={{ base: 12, md: 5 }}><Skeleton height={400} /></Grid.Col></Grid>
        </Stack>
      </FadeIn>
    );
  }

  const StatCard = ({ icon, color, label, value, onClick }: { icon: React.ReactNode; color: string; label: string; value: number; onClick?: () => void }) => (
    <Card shadow="sm" padding="lg" radius="md" withBorder style={{ cursor: onClick ? 'pointer' : undefined }} onClick={onClick}>
      <Group wrap="nowrap">
        <ThemeIcon size={48} radius="md" variant="light" color={color}>{icon}</ThemeIcon>
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
          <Text size="xl" fw={700}>{value}</Text>
        </div>
      </Group>
    </Card>
  );

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <div>
            <Title order={2}>Tasks Dashboard</Title>
            <Text size="sm" c="dimmed">Overview of your tasks and activities</Text>
          </div>
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={() => refetch()}><IconRefresh size={18} /></ActionIcon>
            <Button variant="light" leftSection={<IconNote size={16} />} onClick={() => setQuickTaskModalOpen(true)}>Quick Task</Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/app/tasks/create')}>New Task</Button>
          </Group>
        </Group>

        {/* Stats Cards - 2x2 grid + overdue full width (matches old DashboardStats) */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <StatCard icon={<IconEye size={24} />} color="blue" label="Unseen" value={stats.unseen} onClick={() => navigate('/app/tasks/assigned-tasks')} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <StatCard icon={<IconEyeCheck size={24} />} color="cyan" label="Seen It" value={stats.seenIt} onClick={() => navigate('/app/tasks/assigned-tasks')} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <StatCard icon={<IconPlayerPlay size={24} />} color="yellow" label="On It" value={stats.onIt} onClick={() => navigate('/app/tasks/assigned-tasks')} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <StatCard icon={<IconCheck size={24} />} color="green" label="Done It" value={stats.doneIt} onClick={() => navigate('/app/tasks/assigned-tasks')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder style={{ cursor: 'pointer', borderLeft: '4px solid var(--mantine-color-red-6)' }} onClick={() => navigate('/app/tasks/assigned-tasks')}>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="filled" color="red"><IconAlertCircle size={24} /></ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Overdue</Text>
                  <Text size="xl" fw={700} c="red">{stats.overdue}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder style={{ cursor: 'pointer', borderLeft: '4px solid var(--mantine-color-orange-5)' }} onClick={() => navigate('/app/tasks/assigned-tasks')}>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="filled" color="orange"><IconClock size={24} /></ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Due This Week</Text>
                  <Text size="xl" fw={700} c="orange">{stats.dueThisWeek}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Two-column: To-Do (left) + Active Tasks (right) - matches old DashboardDueSoon + active */}
        <Grid>
          {/* To-Do Tasks (Due Soon) */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <IconClock size={20} />
                  <Title order={4}>To-Do Tasks</Title>
                </Group>
                <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />} onClick={() => navigate('/app/tasks/assigned-tasks')}>
                  View All
                </Button>
              </Group>
              <Divider mb="sm" />
              <ScrollArea h={380}>
                {todoTasks.length === 0 ? (
                  <EmptyState title="No tasks due soon" />
                ) : (
                  <Stack gap="xs">
                    {todoTasks.map((task) => (
                      <Card key={task.taskID} padding="sm" withBorder radius="sm" style={{ cursor: 'pointer' }} onClick={() => goToTask(task.taskID)}>
                        <Group justify="space-between" wrap="nowrap">
                          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                            <Group gap={6}>
                              <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>{task.title || 'Untitled'}</Text>
                              <SelfTaskBadge task={task} />
                            </Group>
                            <Group gap={6}>
                              <TaskStatusBadge statusId={task.statusID} size="xs" />
                              <TaskPriorityBadge priorityId={task.priorityID} size="xs" />
                            </Group>
                          </Stack>
                          <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                            <DueDateDisplay dueDate={task.dueDate} statusID={task.statusID} isOverdue={task.isOverdue} size="xs" showDaysUntil={false} />
                            <Text size="xs" c="dimmed">{task.createdByName || ''}</Text>
                          </Stack>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                )}
              </ScrollArea>
            </Card>
          </Grid.Col>

          {/* Active / In Progress */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <IconPlayerPlay size={20} />
                  <Title order={4}>In Progress</Title>
                  {activeTasks.length > 0 && <Badge size="sm" color="yellow" variant="filled">{activeTasks.length}</Badge>}
                </Group>
              </Group>
              <Divider mb="sm" />
              <ScrollArea h={380}>
                {activeTasks.length === 0 ? (
                  <EmptyState title="No tasks in progress" />
                ) : (
                  <Stack gap="xs">
                    {activeTasks.map((task) => (
                      <Card
                        key={task.taskID} padding="sm" withBorder radius="sm"
                        style={{ cursor: 'pointer', borderLeft: task.isOverdue ? '3px solid var(--mantine-color-red-5)' : '3px solid var(--mantine-color-yellow-5)' }}
                        onClick={() => goToTask(task.taskID)}
                      >
                        <Stack gap={2}>
                          <Group gap={6}>
                            <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>{task.title || 'Untitled'}</Text>
                            <SelfTaskBadge task={task} />
                          </Group>
                          <Group gap={6} justify="space-between">
                            <Group gap={4}>
                              <TaskStatusBadge statusId={task.statusID} size="xs" />
                              <TaskPriorityBadge priorityId={task.priorityID} size="xs" />
                            </Group>
                            <DueDateDisplay dueDate={task.dueDate} statusID={task.statusID} isOverdue={task.isOverdue} size="xs" showDaysUntil={false} />
                          </Group>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </ScrollArea>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Task Summary by Status
            Compact, scannable layout: one row per status with a colored side
            rail, label, count, distribution bar (% of total), and an open/close
            chevron. Each row expands to reveal the first ~20 tasks. */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <ThemeIcon size="md" radius="md" variant="light" color="violet">
                <IconChecklist size={16} />
              </ThemeIcon>
              <Stack gap={0}>
                <Title order={4}>Task Summary</Title>
                <Text size="xs" c="dimmed">Breakdown of your tasks by status</Text>
              </Stack>
            </Group>
            <Badge variant="light" color="violet" size="lg">{allTasks.length} total</Badge>
          </Group>

          {(() => {
            const order = [-2, -1, 0, 1, 2, 3, 4];
            const summaryRows = statuses
              .filter((s) => (tasksByStatus[s.statusID]?.length ?? 0) > 0)
              .sort((a, b) => order.indexOf(a.statusID) - order.indexOf(b.statusID));

            if (summaryRows.length === 0) {
              return (
                <EmptyState
                  icon={<IconChecklist size={40} />}
                  title="No tasks yet"
                  description="Create a task to see the breakdown by status."
                />
              );
            }

            const total = allTasks.length || 1;

            return (
              <Accordion
                multiple={true as any}
                chevronPosition="right"
                variant="separated"
                radius="md"
                styles={{
                  item: {
                    border: '1px solid var(--mantine-color-default-border)',
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                  },
                  control: { paddingTop: 10, paddingBottom: 10 },
                  panel: { borderTop: '1px solid var(--mantine-color-default-border)' },
                  content: { paddingTop: 0, paddingBottom: 0 },
                }}
              >
                {summaryRows.map((status) => {
                  const group = tasksByStatus[status.statusID] || [];
                  const count = group.length;
                  const pct = Math.round((count / total) * 100);
                  // Hex -> mantine color name via the same map used in TaskStatusBadge.
                  const hex = (status.color || '').toUpperCase();
                  const railColor = ({
                    '#FF0000': 'red',
                    '#FFA500': 'orange',
                    '#0000FF': 'blue',
                    '#26BAD9': 'cyan',
                    '#FFFF00': 'yellow',
                    '#00FF00': 'green',
                    '#808080': 'gray',
                  } as Record<string, string>)[hex] || 'gray';

                  return (
                    <Accordion.Item key={status.statusID} value={String(status.statusID)}>
                      <Accordion.Control>
                        <Group justify="space-between" wrap="nowrap" gap="md" style={{ width: '100%' }}>
                          {/* Left: status badge + count */}
                          <Group gap="sm" wrap="nowrap" style={{ minWidth: 180 }}>
                            <div style={{
                              width: 4,
                              height: 28,
                              borderRadius: 2,
                              backgroundColor: `var(--mantine-color-${railColor}-6)`,
                              flexShrink: 0,
                            }} />
                            <TaskStatusBadge statusId={status.statusID} size="md" />
                            <Text size="sm" fw={700}>{count}</Text>
                          </Group>

                          {/* Center: distribution bar — only meaningful when total > 0 */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              width: '100%',
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: 'var(--mantine-color-default-border)',
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${pct}%`,
                                height: '100%',
                                backgroundColor: `var(--mantine-color-${railColor}-6)`,
                                transition: 'width 220ms ease',
                              }} />
                            </div>
                          </div>

                          {/* Right: percentage */}
                          <Text size="xs" c="dimmed" fw={600} style={{ width: 48, textAlign: 'right', flexShrink: 0 }}>
                            {pct}%
                          </Text>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap={0}>
                          {group.slice(0, 20).map((task, idx) => (
                            <Group
                              key={task.taskID}
                              justify="space-between"
                              wrap="nowrap"
                              px="md"
                              py="xs"
                              style={{
                                borderBottom: idx === Math.min(group.length, 20) - 1 ? 'none' : '1px solid var(--mantine-color-default-border)',
                                cursor: 'pointer',
                                transition: 'background-color 100ms ease',
                              }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--mantine-color-default-hover)'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                              onClick={() => goToTask(task.taskID)}
                            >
                              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" fw={500} lineClamp={1}>{task.title || 'Untitled'}</Text>
                                {(task.departmentName || task.projectName) && (
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {[task.departmentName, task.projectName].filter(Boolean).join(' · ')}
                                  </Text>
                                )}
                              </Stack>
                              <Group gap="sm" style={{ flexShrink: 0 }}>
                                <TaskPriorityBadge priorityId={task.priorityID} size="xs" />
                                <DueDateDisplay dueDate={task.dueDate} statusID={task.statusID} isOverdue={task.isOverdue} size="xs" showDaysUntil={false} />
                                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                                  <Avatar size="xs" radius="xl" color="blue">{(task.createdByName?.[0] || '?')}</Avatar>
                                  <Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 120 }}>{task.createdByName || '-'}</Text>
                                </Group>
                              </Group>
                            </Group>
                          ))}
                          {group.length > 20 && (
                            <Group justify="center" py="xs">
                              <Button
                                variant="subtle"
                                size="compact-xs"
                                rightSection={<IconArrowRight size={12} />}
                                onClick={() => navigate('/app/tasks/my-tasks')}
                              >
                                View remaining {group.length - 20}
                              </Button>
                            </Group>
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            );
          })()}
        </Card>
      </Stack>
    </FadeIn>
  );
}
